import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import { toast } from "react-toastify";
import useKothar from "context/useKothar";
import ViewClientProfile from "components/Clients/ViewClientProfile";
import ReferrerChangeDialog from "views/admin/Clients/ReferrerChangeDialog";
import { fetchAssignableReferralPartners } from "views/admin/Clients/referralPicker";
import { buildLinkCandidates } from "./linkCandidates";
import { getReferrerReview, linkCustomReferrer } from "../referrerReviewApi";

const money = (v) => Number(v || 0).toFixed(2);

// Search by email as well as name: the email is what tells two people with one name apart.
const filterCandidates = createFilterOptions({
  stringify: (option) => `${option.label} ${option.sublabel}`,
});

const LinkDialog = ({ group, users, onClose, onDone }) => {
  const { data: partners = [] } = useQuery(
    ["referral-partners-assignable"],
    fetchAssignableReferralPartners
  );
  const candidates = useMemo(
    () => buildLinkCandidates({ suggestions: group.suggestions, users, partners }),
    [group.suggestions, users, partners]
  );
  // Mounted per group (see the key where it is rendered), so these defaults follow the group.
  const [selected, setSelected] = useState(() =>
    group.suggestions?.[0]
      ? buildLinkCandidates({ suggestions: group.suggestions.slice(0, 1) })[0] ?? null
      : null
  );
  const userId = selected?.userId ?? null;
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      const result = await linkCustomReferrer({
        label: group.label,
        userId: Number(userId),
        reason: reason.trim(),
      });
      toast.success(`Linked ${result.linked} client(s)`);
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not link the referrer");
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open onClose={saving ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle>Link “{group.label}”</DialogTitle>
      <DialogContent className="space-y-3">
        <p className="text-sm m-0">
          {group.clientCount} client(s) name this referrer. Their pre-portal referral amounts (
          {money(group.snapshotAmount)}) will show as paid for the user you pick.
        </p>
        {!group.suggestions?.length && (
          <p className="text-xs text-app-text-muted m-0">
            No user matches this name. Search staff or referral partners by name or email.
          </p>
        )}
        <Autocomplete
          options={candidates}
          groupBy={(option) => option.group}
          filterOptions={filterCandidates}
          getOptionLabel={(option) => option.label || ""}
          isOptionEqualToValue={(option, value) => option.userId === value?.userId}
          value={selected}
          onChange={(_, value) => setSelected(value)}
          renderOption={(props, option) => (
            <li {...props} key={option.userId}>
              <div className="min-w-0">
                <div>{option.label}</div>
                {option.sublabel ? (
                  <div className="text-xs text-app-text-muted truncate">{option.sublabel}</div>
                ) : null}
              </div>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Referrer"
              helperText={selected?.sublabel ? `Selected: ${selected.sublabel}` : undefined}
            />
          )}
        />
        <TextField
          label="Reason"
          fullWidth
          multiline
          minRows={2}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={submit} disabled={saving || !userId || !reason.trim()}>
          {`Link ${group.clientCount} clients`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const Section = ({ title, hint, children }) => (
  <div className="bg-app-surface rounded-lg border border-app-border p-4 space-y-3">
    <div>
      <h3 className="text-base font-bold m-0">{title}</h3>
      <p className="text-xs text-app-text-muted m-0">{hint}</p>
    </div>
    <div className="overflow-x-auto">{children}</div>
  </div>
);

const ReferrerReview = () => {
  const queryClient = useQueryClient();
  const [{ usersList }] = useKothar();
  const orgId = JSON.parse(localStorage.getItem("userDetail") || "{}")?.orgId;
  const orgUsers = (usersList || []).filter((user) => user?.organization?.id === orgId);
  const { data, isLoading, error } = useQuery(["referrer-review"], getReferrerReview);
  const [linking, setLinking] = useState(null);
  const [changing, setChanging] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState([]);
  const refresh = () => queryClient.invalidateQueries(["referrer-review"]);

  const toggleGroup = (label) =>
    setExpandedGroups((current) =>
      current.includes(label) ? current.filter((open) => open !== label) : [...current, label]
    );

  if (isLoading) return <CircularProgress />;
  if (error)
    return (
      <p className="text-sm text-rose-700">
        {error?.response?.data?.message || "Could not load the review"}
      </p>
    );

  return (
    <div className="space-y-4 p-4">
      <h2 className="text-xl font-bold m-0">Referrer review</h2>
      <Section
        title="Custom referrers"
        hint="Names typed before the portal. Link one to a staff member or referral partner to bring their amounts in as paid."
      >
        <table className="w-full text-sm">
          <tbody>
            {data.customGroups.map((g) => (
              <tr key={g.label}>
                <td>
                  <div>{g.label}</div>
                  {expandedGroups.includes(g.label) && g.clients?.length ? (
                    <ul className="m-0 mt-1 space-y-0.5 p-0 list-none">
                      {g.clients.map((client) => (
                        <li key={client.id}>
                          <ViewClientProfile
                            variant="link"
                            clientId={client.id}
                            displayName={client.name}
                          />
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </td>
                <td>
                  {g.clients?.length ? (
                    <button
                      type="button"
                      onClick={() => toggleGroup(g.label)}
                      aria-expanded={expandedGroups.includes(g.label)}
                      className="bg-transparent border-0 p-0 cursor-pointer text-primary hover:underline text-sm"
                    >
                      {g.clientCount} client(s) {expandedGroups.includes(g.label) ? "▴" : "▾"}
                    </button>
                  ) : (
                    <>{g.clientCount} client(s)</>
                  )}
                </td>
                <td>{money(g.snapshotAmount)}</td>
                <td>
                  {g.suggestions.length
                    ? g.suggestions.map((s) => `${s.name} <${s.email}>`).join(", ")
                    : "No suggestion"}
                </td>
                <td>
                  <Button size="small" onClick={() => setLinking(g)}>
                    Link
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>
      <Section
        title="Conflicts"
        hint="Legacy records disagree about who referred these clients. Pick the right one."
      >
        <table className="w-full text-sm">
          <tbody>
            {data.conflicts.map((c) => (
              <tr key={c.clientId}>
                <td>
                  <ViewClientProfile
                    variant="link"
                    clientId={c.clientId}
                    displayName={c.clientName}
                  />
                </td>
                <td>
                  {c.candidates
                    .map((x) => `${x.email} (${x.source}${x.member ? "" : ", not a member"})`)
                    .join(" · ")}
                </td>
                <td>
                  <Button
                    size="small"
                    onClick={() => setChanging({ id: c.clientId, referral: null })}
                  >
                    Choose referrer
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>
      <Section
        title="Amounts without a referrer"
        hint="Referral amounts exist but no referrer is recorded."
      >
        <table className="w-full text-sm">
          <tbody>
            {data.unassigned.map((u) => (
              <tr key={u.clientId}>
                <td>
                  <ViewClientProfile
                    variant="link"
                    clientId={u.clientId}
                    displayName={u.clientName}
                  />
                </td>
                <td>{money(u.snapshotAmount)}</td>
                <td>
                  <Button
                    size="small"
                    onClick={() => setChanging({ id: u.clientId, referral: null })}
                  >
                    Assign referrer
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>
      {linking && (
        <LinkDialog
          key={linking.label}
          group={linking}
          users={orgUsers}
          onClose={() => setLinking(null)}
          onDone={() => {
            setLinking(null);
            refresh();
          }}
        />
      )}
      <ReferrerChangeDialog
        open={!!changing}
        client={changing}
        users={orgUsers}
        onClose={() => setChanging(null)}
        onChanged={() => {
          setChanging(null);
          refresh();
        }}
      />
    </div>
  );
};

export default ReferrerReview;
